import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import axios from 'axios';
import { portfolioService } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  mapPortfolioToProPage,
  mapProPageFormToPortfolio,
  getApiErrorMessage,
  unwrapApiData,
  createEmptySocialFormFields,
} from '../../utils/proPageMapper';
import { SOCIAL_NETWORKS, mapSocialLinksToForm } from '../../utils/socialLinks';
import {
  Globe, Image as ImageIcon, Plus, X, Eye, EyeOff, Save, ExternalLink,
  Briefcase, GraduationCap, Languages, Euro, Link2, Trash2,
  Camera, CheckCircle2, AlertCircle, Rocket, Share2
} from 'lucide-react';

import API from '../../constants/api'
// API base via getApiBase()
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const IMAGE_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const validateImageFile = (file) => {
  if (!file) return 'Aucun fichier sélectionné';
  if (file.size > IMAGE_MAX_BYTES) return 'Le fichier ne doit pas dépasser 5 Mo';
  if (!IMAGE_ALLOWED_TYPES.includes(file.type)) {
    return 'Format non supporté. Utilisez JPEG, PNG ou WebP';
  }
  return null;
};

const getUploadedImageUrl = (response) =>
  response?.data?.url
  ?? response?.data?.data?.url
  ?? response?.url
  ?? '';

export default function MyProPage() {
  const { user, token, hasModule, isProView } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proPage, setProPage] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  const [formData, setFormData] = useState({
    cover_image_url: '',
    profile_image_url: '',
    headline: '',
    bio: '',
    tarif_journalier: '',
    tarif_horaire: '',
    competences: [],
    langues: [],
    experiences: [],
    formations: [],
    website_url: '',
    is_published: false,
    show_contact_form: true,
    show_tarifs: true,
    ...createEmptySocialFormFields(),
  });

  const [newCompetence, setNewCompetence] = useState('');
  const [newLangue, setNewLangue] = useState('');
  const [newExperience, setNewExperience] = useState({ title: '', company: '', start_date: '', end_date: '', description: '' });
  const [newFormation, setNewFormation] = useState({ title: '', school: '', year: '', description: '' });
  const [newPortfolioItem, setNewPortfolioItem] = useState({ title: '', description: '', image_url: '', category: '' });
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingPortfolioImage, setUploadingPortfolioImage] = useState(false);

  const coverInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const portfolioImageInputRef = useRef(null);

  const isPro = isProView();

  const authHeaders = { Authorization: `Bearer ${token}` };

  const applyProPagePayload = (payload) => {
    const mapped = mapPortfolioToProPage(payload, user);
    setProPage(mapped);
    setFormData({
      cover_image_url: mapped.cover_image_url || '',
      profile_image_url: mapped.profile_image_url || '',
      headline: mapped.headline || '',
      bio: mapped.bio || '',
      tarif_journalier: mapped.tarif_journalier ?? '',
      tarif_horaire: mapped.tarif_horaire ?? '',
      competences: mapped.competences || [],
      langues: mapped.langues || [],
      experiences: mapped.experiences || [],
      formations: mapped.formations || [],
      website_url: mapped.website_url || '',
      is_published: mapped.is_published || false,
      show_contact_form: mapped.show_contact_form !== false,
      show_tarifs: mapped.show_tarifs !== false,
      ...mapSocialLinksToForm(mapped.social_links || {}),
    });
    return mapped;
  };

  useEffect(() => {
    if (token) fetchProPage();
  }, [token]);

  const fetchProPage = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/users/me/portfolio`, {
        headers: authHeaders,
      });
      applyProPagePayload(unwrapApiData(response));
    } catch (error) {
      console.error('Error fetching pro page:', error);
      toast.error(getApiErrorMessage(error, 'Impossible de charger votre One Page'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const portfolioData = mapProPageFormToPortfolio(formData, proPage?.portfolio ?? []);
      const response = await axios.patch(
        `${API}/users/me/portfolio`,
        { portfolioData },
        { headers: authHeaders }
      );

      applyProPagePayload(unwrapApiData(response));
      toast.success('Page enregistrée avec succès !');
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    try {
      const response = await axios.post(
        `${API}/users/me/portfolio/publish`,
        { published: !formData.is_published },
        { headers: authHeaders }
      );

      const payload = unwrapApiData(response);
      setFormData((prev) => ({ ...prev, is_published: payload.is_published }));
      setProPage((prev) => (prev ? { ...prev, is_published: payload.is_published } : prev));
      toast.success(payload.message || (payload.is_published ? 'Page publiée' : 'Page dépubliée'));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la publication'));
    }
  };

  const persistPortfolioForm = async (nextFormData) => {
    const portfolioData = mapProPageFormToPortfolio(nextFormData, proPage?.portfolio ?? []);
    const response = await axios.patch(
      `${API}/users/me/portfolio`,
      { portfolioData },
      { headers: authHeaders }
    );
    applyProPagePayload(unwrapApiData(response));
  };

  const patchPortfolioPhotos = async (portfolioItems) => {
    const portfolioData = mapProPageFormToPortfolio(formData, portfolioItems);
    const response = await axios.patch(
      `${API}/users/me/portfolio`,
      { portfolioData },
      { headers: authHeaders }
    );
    applyProPagePayload(unwrapApiData(response));
  };

  const uploadPortfolioImage = async (file, type) => {
    const validationError = validateImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return null;
    }

    const response = await portfolioService.uploadImage(file, type);
    const url = getUploadedImageUrl(response);
    if (!url) {
      throw new Error('URL de l\'image introuvable après upload');
    }
    return url;
  };

  const handleCoverImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingCover(true);
    try {
      const url = await uploadPortfolioImage(file, 'cover');
      if (url) {
        const nextFormData = { ...formData, cover_image_url: url };
        setFormData(nextFormData);
        await persistPortfolioForm(nextFormData);
        toast.success('Image de couverture enregistrée');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors du chargement de l\'image'));
    } finally {
      setUploadingCover(false);
    }
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingProfile(true);
    try {
      const url = await uploadPortfolioImage(file, 'profile');
      if (url) {
        const nextFormData = { ...formData, profile_image_url: url };
        setFormData(nextFormData);
        await persistPortfolioForm(nextFormData);
        toast.success('Photo de profil enregistrée');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors du chargement de la photo'));
    } finally {
      setUploadingProfile(false);
    }
  };

  const handlePortfolioImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingPortfolioImage(true);
    try {
      const url = await uploadPortfolioImage(file, 'portfolio');
      if (url) {
        setNewPortfolioItem((prev) => ({ ...prev, image_url: url }));
        toast.success('Image chargée');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors du chargement de l\'image'));
    } finally {
      setUploadingPortfolioImage(false);
    }
  };

  const addCompetence = () => {
    if (newCompetence && !formData.competences.includes(newCompetence)) {
      setFormData(prev => ({
        ...prev,
        competences: [...prev.competences, newCompetence]
      }));
      setNewCompetence('');
    }
  };

  const removeCompetence = (comp) => {
    setFormData(prev => ({
      ...prev,
      competences: prev.competences.filter(c => c !== comp)
    }));
  };

  const addLangue = () => {
    if (newLangue && !formData.langues.includes(newLangue)) {
      setFormData(prev => ({
        ...prev,
        langues: [...prev.langues, newLangue]
      }));
      setNewLangue('');
    }
  };

  const removeLangue = (lang) => {
    setFormData(prev => ({
      ...prev,
      langues: prev.langues.filter(l => l !== lang)
    }));
  };

  const addExperience = () => {
    if (newExperience.title && newExperience.company) {
      setFormData(prev => ({
        ...prev,
        experiences: [...prev.experiences, { ...newExperience }]
      }));
      setNewExperience({ title: '', company: '', start_date: '', end_date: '', description: '' });
    }
  };

  const removeExperience = (index) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter((_, i) => i !== index)
    }));
  };

  const addFormation = () => {
    if (newFormation.title && newFormation.school) {
      setFormData(prev => ({
        ...prev,
        formations: [...prev.formations, { ...newFormation }]
      }));
      setNewFormation({ title: '', school: '', year: '', description: '' });
    }
  };

  const removeFormation = (index) => {
    setFormData(prev => ({
      ...prev,
      formations: prev.formations.filter((_, i) => i !== index)
    }));
  };

  const addPortfolioItem = async () => {
    if (!newPortfolioItem.title) {
      toast.error('Le titre est requis');
      return;
    }

    if (!newPortfolioItem.image_url) {
      toast.error("L'image est requise");
      return;
    }
    
    try {
      const nextItems = [
        ...(proPage?.portfolio ?? []),
        {
          id: `photo_${Date.now()}`,
          title: newPortfolioItem.title,
          category: newPortfolioItem.category,
          description: newPortfolioItem.description,
          image_url: newPortfolioItem.image_url,
          publicId: `external_photo_${Date.now()}`,
        },
      ];

      setSaving(true);
      await patchPortfolioPhotos(nextItems);
      setNewPortfolioItem({ title: '', description: '', image_url: '', category: '' });
      toast.success('Réalisation ajoutée !');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'ajout au portfolio'));
    } finally {
      setSaving(false);
    }
  };

  const removePortfolioItem = async (itemId) => {
    try {
      const nextItems = (proPage?.portfolio ?? []).filter((item) => item.id !== itemId);
      await patchPortfolioPhotos(nextItems);
      toast.success('Réalisation supprimée');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  // Non-Pro redirect
  if (!isPro) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-20 h-20 bg-[hsl(var(--primary)/0.08)] rounded-full flex items-center justify-center mx-auto mb-6">
            <Rocket className="w-10 h-10 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Fonctionnalité Pro</h1>
          <p className="text-slate-600 mb-6">
            La One Page est réservée aux comptes professionnels. Passez en Pro pour créer votre page publique personnalisée !
          </p>
          <Button 
            className="btn-clickpro-primary"
            onClick={() => window.location.href = '/tableau-de-bord/profil'}
          >
            <Rocket className="w-4 h-4 mr-2" />
            Passer en Pro
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[hsl(var(--primary))]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'general', label: 'Général', icon: Globe },
    { id: 'about', label: 'À propos', icon: Briefcase },
    { id: 'experience', label: 'Parcours', icon: GraduationCap },
    { id: 'portfolio', label: 'Portfolio', icon: ImageIcon },
    { id: 'settings', label: 'Paramètres', icon: CheckCircle2 }
  ];

  return (
    <DashboardLayout>
      <div className="w-full" data-testid="my-pro-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Ma One Page</h1>
            <p className="text-slate-600">Personnalisez votre page publique professionnelle</p>
          </div>
          
          <div className="flex items-center gap-3">
            {proPage?.slug && (
              <a
                href={`https://clicketpro.fr/artisans/${proPage.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[hsl(var(--primary))] hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                Voir ma page
              </a>
            )}
            
            <Button
              variant={formData.is_published ? "outline" : "default"}
              onClick={handleTogglePublish}
              disabled={!proPage}
              data-testid="publish-toggle"
            >
              {formData.is_published ? (
                <><EyeOff className="w-4 h-4 mr-2" /> Dépublier</>
              ) : (
                <><Eye className="w-4 h-4 mr-2" /> Publier</>
              )}
            </Button>
            
            <Button onClick={handleSave} disabled={saving} className="btn-clickpro-primary" data-testid="save-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Status Banner */}
        {proPage && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            formData.is_published 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-amber-50 border border-amber-200'
          }`}>
            {formData.is_published ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Page publiée</p>
                  <p className="text-sm text-green-600">
                    Accessible sur : <a href={`https://clicketpro.fr/artisans/${proPage.slug}`} target="_blank" rel="noopener noreferrer" className="underline">clicketpro.fr/artisans/{proPage.slug}</a>
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Page non publiée</p>
                  <p className="text-sm text-amber-600">Votre page n&apos;est pas visible publiquement</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="lux-dashboard-card p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Images & Présentation</h2>
              
              {/* Cover Image */}
              <div>
                <Label className="mb-2 block">Image de couverture</Label>
                <div className="relative h-40 bg-slate-100 rounded-xl overflow-hidden mb-2">
                  {formData.cover_image_url ? (
                    <img src={formData.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <Camera className="w-12 h-12" />
                    </div>
                  )}
                </div>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleCoverImageChange}
                  data-testid="cover-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover}
                  data-testid="cover-upload-btn"
                >
                  {uploadingCover ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      {formData.cover_image_url ? 'Changer l\'image' : 'Charger une image'}
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 mt-1">JPEG, PNG ou WebP — max 5 Mo — 1600×400 px recommandé</p>
              </div>

              {/* Profile Image */}
              <div>
                <Label className="mb-2 block">Photo de profil</Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0">
                    {formData.profile_image_url ? (
                      <img src={formData.profile_image_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Camera className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={profileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleProfileImageChange}
                      data-testid="profile-file"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => profileInputRef.current?.click()}
                      disabled={uploadingProfile}
                      data-testid="profile-upload-btn"
                    >
                      {uploadingProfile ? (
                        <>
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2" />
                          Chargement...
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-2" />
                          {formData.profile_image_url ? 'Changer la photo' : 'Charger une photo'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 mt-1">JPEG, PNG ou WebP — max 5 Mo — 400×400 px recommandé</p>
                  </div>
                </div>
              </div>

              {/* Headline */}
              <div>
                <Label className="mb-2 block">Titre accrocheur</Label>
                <Input
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="Ex: Plombier expert en dépannage rapide sur Paris"
                  maxLength={150}
                  data-testid="headline"
                />
                <p className="text-xs text-slate-500 mt-1">{formData.headline.length}/150 caractères</p>
              </div>

              {/* Tarifs */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Tarif journalier (€)
                  </Label>
                  <Input
                    type="number"
                    value={formData.tarif_journalier}
                    onChange={(e) => setFormData({ ...formData, tarif_journalier: e.target.value })}
                    placeholder="Ex: 400"
                    data-testid="tarif-jour"
                  />
                </div>
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Tarif horaire (€)
                  </Label>
                  <Input
                    type="number"
                    value={formData.tarif_horaire}
                    onChange={(e) => setFormData({ ...formData, tarif_horaire: e.target.value })}
                    placeholder="Ex: 60"
                    data-testid="tarif-heure"
                  />
                </div>
              </div>

              {/* Links & réseaux sociaux */}
              <div className="space-y-4 pt-2">
                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Share2 className="w-4 h-4" />
                    Réseaux sociaux
                  </Label>
                  <p className="text-xs text-slate-500 mb-4">
                    Ajoutez vos liens publics pour que vos clients puissent vous suivre.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {SOCIAL_NETWORKS.map((network) => {
                      const Icon = network.icon
                      const fieldKey = `${network.key}_url`
                      return (
                        <div key={network.key}>
                          <Label className="mb-2 block flex items-center gap-2 text-sm">
                            <Icon className="w-4 h-4" style={{ color: network.color }} />
                            {network.label}
                          </Label>
                          <Input
                            type="url"
                            value={formData[fieldKey] || ''}
                            onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
                            placeholder={network.placeholder}
                            data-testid={`social-${network.key}-input`}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Site web
                  </Label>
                  <Input
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://www.monsite.fr"
                    data-testid="website-url-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* About Tab */}
          {activeTab === 'about' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">À propos de vous</h2>
              
              {/* Bio */}
              <div>
                <Label className="mb-2 block">Biographie</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Présentez-vous, votre parcours, vos spécialités..."
                  rows={8}
                  maxLength={3000}
                  data-testid="bio"
                />
                <p className="text-xs text-slate-500 mt-1">{formData.bio.length}/3000 caractères</p>
              </div>

              {/* Compétences */}
              <div>
                <Label className="mb-2 block">Compétences</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newCompetence}
                    onChange={(e) => setNewCompetence(e.target.value)}
                    placeholder="Ajouter une compétence"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCompetence())}
                  />
                  <Button type="button" variant="outline" onClick={addCompetence}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.competences.map((comp, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1 px-3 py-1.5">
                      {comp}
                      <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeCompetence(comp)} />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Langues */}
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  Langues
                </Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newLangue}
                    onChange={(e) => setNewLangue(e.target.value)}
                    placeholder="Ex: Français, Anglais..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLangue())}
                  />
                  <Button type="button" variant="outline" onClick={addLangue}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.langues.map((lang, i) => (
                    <Badge key={i} variant="outline" className="flex items-center gap-1 px-3 py-1.5">
                      {lang}
                      <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeLangue(lang)} />
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Experience Tab */}
          {activeTab === 'experience' && (
            <div className="space-y-8">
              {/* Expériences */}
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[hsl(var(--primary))]" />
                  Expériences professionnelles
                </h2>
                
                {/* Add Experience Form */}
                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <Input
                      value={newExperience.title}
                      onChange={(e) => setNewExperience({ ...newExperience, title: e.target.value })}
                      placeholder="Poste / Titre"
                    />
                    <Input
                      value={newExperience.company}
                      onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })}
                      placeholder="Entreprise"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <Input
                      value={newExperience.start_date}
                      onChange={(e) => setNewExperience({ ...newExperience, start_date: e.target.value })}
                      placeholder="Date début (ex: Jan 2020)"
                    />
                    <Input
                      value={newExperience.end_date}
                      onChange={(e) => setNewExperience({ ...newExperience, end_date: e.target.value })}
                      placeholder="Date fin (ou vide si actuel)"
                    />
                  </div>
                  <Textarea
                    value={newExperience.description}
                    onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })}
                    placeholder="Description (optionnel)"
                    rows={2}
                    className="mb-4"
                  />
                  <Button type="button" onClick={addExperience} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter cette expérience
                  </Button>
                </div>

                {/* Experience List */}
                <div className="space-y-3">
                  {formData.experiences.map((exp, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 lux-dashboard-card">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{exp.title}</h4>
                        <p className="text-[hsl(var(--primary))] text-sm">{exp.company}</p>
                        <p className="text-slate-500 text-sm">{exp.start_date} - {exp.end_date || 'Présent'}</p>
                        {exp.description && <p className="text-slate-600 text-sm mt-2">{exp.description}</p>}
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeExperience(i)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Formations */}
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[hsl(var(--primary))]" />
                  Formations
                </h2>
                
                {/* Add Formation Form */}
                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <Input
                      value={newFormation.title}
                      onChange={(e) => setNewFormation({ ...newFormation, title: e.target.value })}
                      placeholder="Diplôme / Formation"
                    />
                    <Input
                      value={newFormation.school}
                      onChange={(e) => setNewFormation({ ...newFormation, school: e.target.value })}
                      placeholder="École / Organisme"
                    />
                  </div>
                  <Input
                    value={newFormation.year}
                    onChange={(e) => setNewFormation({ ...newFormation, year: e.target.value })}
                    placeholder="Année (ex: 2020)"
                    className="mb-4"
                  />
                  <Button type="button" onClick={addFormation} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter cette formation
                  </Button>
                </div>

                {/* Formation List */}
                <div className="space-y-3">
                  {formData.formations.map((edu, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 lux-dashboard-card">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{edu.title}</h4>
                        <p className="text-slate-600 text-sm">{edu.school}</p>
                        <p className="text-slate-500 text-sm">{edu.year}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeFormation(i)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Tab */}
          {activeTab === 'portfolio' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[hsl(var(--primary))]" />
                Portfolio - Réalisations
              </h2>

              {/* Add Portfolio Item Form */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Input
                    value={newPortfolioItem.title}
                    onChange={(e) => setNewPortfolioItem({ ...newPortfolioItem, title: e.target.value })}
                    placeholder="Titre de la réalisation *"
                  />
                  <Input
                    value={newPortfolioItem.category}
                    onChange={(e) => setNewPortfolioItem({ ...newPortfolioItem, category: e.target.value })}
                    placeholder="Catégorie (ex: Rénovation)"
                  />
                </div>
                <div className="mb-4">
                  <input
                    ref={portfolioImageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePortfolioImageChange}
                    data-testid="portfolio-file"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => portfolioImageInputRef.current?.click()}
                      disabled={uploadingPortfolioImage}
                      data-testid="portfolio-upload-btn"
                    >
                      {uploadingPortfolioImage ? (
                        <>
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2" />
                          Chargement...
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-4 h-4 mr-2" />
                          {newPortfolioItem.image_url ? 'Changer l\'image' : 'Charger une image *'}
                        </>
                      )}
                    </Button>
                    {newPortfolioItem.image_url && (
                      <div className="w-16 h-12 rounded-lg overflow-hidden border border-slate-200">
                        <img
                          src={newPortfolioItem.image_url}
                          alt="Aperçu"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <Textarea
                  value={newPortfolioItem.description}
                  onChange={(e) => setNewPortfolioItem({ ...newPortfolioItem, description: e.target.value })}
                  placeholder="Description (optionnel)"
                  rows={2}
                  className="mb-4"
                />
                <Button type="button" onClick={addPortfolioItem} className="btn-clickpro-primary" disabled={saving || uploadingPortfolioImage}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter au portfolio
                </Button>
              </div>

              {/* Portfolio Grid */}
              {proPage?.portfolio?.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {proPage.portfolio.map((item) => (
                    <div key={item.id} className="relative group rounded-xl overflow-hidden border border-slate-200">
                      <div className="aspect-[4/3] bg-slate-100">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <ImageIcon className="w-12 h-12" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-semibold text-slate-900 text-sm truncate">{item.title}</h4>
                        {item.category && (
                          <p className="text-slate-500 text-xs">{item.category}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removePortfolioItem(item.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune réalisation dans votre portfolio</p>
                  <p className="text-slate-400 text-sm">Ajoutez vos projets pour montrer votre travail</p>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Paramètres de la page</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-900">Afficher le formulaire de contact</p>
                    <p className="text-sm text-slate-500">Permet aux visiteurs de vous contacter</p>
                  </div>
                  <Switch
                    checked={formData.show_contact_form}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_contact_form: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-900">Afficher les tarifs</p>
                    <p className="text-sm text-slate-500">Montrer vos tarifs indicatifs sur la page</p>
                  </div>
                  <Switch
                    checked={formData.show_tarifs}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_tarifs: checked })}
                  />
                </div>
              </div>

              {proPage?.slug && (
                <div className="mt-8 p-4 bg-[hsl(var(--primary)/0.08)] rounded-xl">
                  <p className="font-medium text-[hsl(var(--primary))] mb-2">URL de votre page</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded-lg text-sm text-slate-700 border">
                      clicketpro.fr/artisans/{proPage.slug}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://clicketpro.fr/artisans/${proPage.slug}`);
                        toast.success('Lien copié !');
                      }}
                    >
                      Copier
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
